import { useState, useEffect, useRef } from "react";
import Logo from "./logo.svg";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, useUser, useClerk } from "@clerk/clerk-react";
import ThemeSwitcher from "../themeSwitcher/ThemeSwitcher";
import LanguageSwitcher from "../languageSwitcher/LanguageSwitcher";

function SignOutIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

function ExternalLinkIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function TopBar({ logo }) {
  const { t } = useTranslation();
  const { isSignedIn } = useAuth();
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const isAdmin = isLoaded && user?.publicMetadata?.role === "admin";
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const close = () => setMenuOpen(false);

  const handleSignOut = async () => {
    close();
    await signOut();
    navigate("/");
  };

  // Close on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) close();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  // Close on Escape
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [menuOpen]);

  const navItems = [
    ...(isSignedIn
      ? [
          ...(isAdmin
            ? [{ key: "dashboard", type: "link", to: "/dashboard", label: t("topBar.dashboard") }]
            : []),
          { key: "profile", type: "link", to: "/profile", label: t("topBar.profile") },
          { key: "projects", type: "link", to: "/projects", label: t("profile.projectsHeading") },
        ]
      : [
          { key: "login", type: "link", to: "/login", label: t("topBar.login") },
          { key: "signup", type: "link", to: "/signup", label: t("topBar.signUp") },
        ]),
    { key: "eulchen", type: "external", href: "https://eulchen.sk", label: t("topBar.eulchenLink") },
  ];

  return (
    /* Relative wrapper — dropdown is absolute inside, so no layout shift */
    <div className="relative z-50 w-full" ref={menuRef}>

      {/* ── Top bar strip ── */}
      <div className="w-full py-4 px-4 sm:px-6">
        <div className="mainCont flex flex-row flex-nowrap items-center justify-between gap-4">

          {/* Logo */}
          <div className="flex min-w-0 flex-1 items-center">
            {logo && (
              <Link
                to="/"
                className="group flex flex-row flex-nowrap items-center gap-x-4"
                onClick={close}
              >
                <img
                  src={Logo}
                  alt={t("topBar.logoAlt")}
                  className="max-h-[18px] shrink-0 transition-opacity duration-200 group-hover:opacity-60"
                />
                <div className="hidden h-[20px] w-px shrink-0 bg-mainDark/20 md:block" />
                <p className="hidden truncate text-mainDark/50 text-xs uppercase tracking-[0.2em] font-light transition-colors duration-200 group-hover:text-mainDark md:block">
                  {t("topBar.tagline")}
                </p>
              </Link>
            )}
          </div>

          {/* Controls — always visible on every breakpoint */}
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <LanguageSwitcher />
            <ThemeSwitcher />
            <div className="mx-1 h-6 w-px bg-mainDark/15" />

            {/* Hamburger */}
            <button
              type="button"
              aria-label={menuOpen ? t("topBar.closeMenu") : t("topBar.openMenu")}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
              className="rounded-full p-2 text-mainDark transition-all duration-200 hover:bg-mainDark/10 hover:text-mainBlue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mainBlue"
            >
              <span className="flex h-5 w-5 flex-col items-center justify-center gap-[4px]" aria-hidden="true">
                <span
                  className="block h-[1.5px] w-[18px] rounded-full bg-current transition-all duration-300 ease-in-out origin-center"
                  style={{ transform: menuOpen ? "translateY(5.5px) rotate(45deg)" : "none" }}
                />
                <span
                  className="block h-[1.5px] w-[18px] rounded-full bg-current transition-all duration-300 ease-in-out"
                  style={{ opacity: menuOpen ? 0 : 1, transform: menuOpen ? "scaleX(0)" : "scaleX(1)" }}
                />
                <span
                  className="block h-[1.5px] w-[18px] rounded-full bg-current transition-all duration-300 ease-in-out origin-center"
                  style={{ transform: menuOpen ? "translateY(-5.5px) rotate(-45deg)" : "none" }}
                />
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Dropdown — absolutely positioned so it floats over page content ── */}
      <div
        className="absolute left-0 right-0 top-full px-4 sm:px-6 overflow-hidden transition-all duration-300 ease-in-out pointer-events-none"
        style={{
          maxHeight: menuOpen ? "480px" : "0px",
          opacity: menuOpen ? 1 : 0,
          transform: menuOpen ? "translateY(0)" : "translateY(-6px)",
        }}
        aria-hidden={!menuOpen}
      >
        <div className="mainCont pb-4 pointer-events-auto flex justify-end">
          <div className="w-full max-w-xs rounded-2xl border border-mainDark/20 bg-mainWhite shadow-result overflow-hidden">

            {/* Nav links */}
            <nav className="flex flex-col p-2">
              {navItems.map(({ key, type, to, href, label }, i) => (
                <div
                  key={key}
                  style={{
                    transition: `opacity 0.2s ease ${menuOpen ? i * 45 + 50 : 0}ms, transform 0.2s ease ${menuOpen ? i * 45 + 50 : 0}ms`,
                    opacity: menuOpen ? 1 : 0,
                    transform: menuOpen ? "translateY(0)" : "translateY(-5px)",
                  }}
                >
                  {type === "link" ? (
                    <Link
                      to={to}
                      onClick={close}
                      className="group flex items-center justify-between rounded-xl px-4 py-3 text-mainDark transition-all duration-200 hover:bg-mainBlue/10 hover:text-mainBlue"
                    >
                      <span className="text-sm uppercase tracking-widest font-light">
                        {label}
                      </span>
                      <span className="h-px w-0 bg-mainBlue transition-all duration-200 group-hover:w-5 rounded-full" />
                    </Link>
                  ) : (
                    <a
                      href={href}
                      onClick={close}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-between rounded-xl px-4 py-3 text-mainDark transition-all duration-200 hover:bg-mainBlue/10 hover:text-mainBlue"
                    >
                      <span className="text-sm uppercase tracking-widest font-light">
                        {label}
                      </span>
                      <ExternalLinkIcon className="h-3 w-3 opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
                    </a>
                  )}
                </div>
              ))}
            </nav>

            {/* Sign-out row */}
            {isSignedIn && (
              <div
                className="border-t border-mainDark/20 p-2"
                style={{
                  transition: `opacity 0.2s ease ${menuOpen ? navItems.length * 45 + 50 : 0}ms, transform 0.2s ease ${menuOpen ? navItems.length * 45 + 50 : 0}ms`,
                  opacity: menuOpen ? 1 : 0,
                  transform: menuOpen ? "translateY(0)" : "translateY(-5px)",
                }}
              >
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="group flex w-full items-center gap-2.5 rounded-xl px-4 py-3 text-mainDark/40 transition-all duration-200 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                >
                  <SignOutIcon className="h-3.5 w-3.5 shrink-0" />
                  <span className="text-sm uppercase tracking-widest font-light">
                    {t("profile.signOut")}
                  </span>
                </button>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

export default TopBar;
